import {
  Box,
  Center,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  InputGroup,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { Trans, Plural } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { SitesLocationsTable } from './SitesLocationsTable';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Pagination } from '@duke-hq/ui/components/Pagination/Pagination';
import { usePagination } from '@duke-hq/ui/components/hooks/usePagination';
import { CenteredSpinner } from '@duke-hq/app-admin-web/components/Loading';
import { SiteLocationsDetails } from './SiteLocationsDetails';
import { LocationError } from './LocationError';
import { StatesFilter } from './RegionSites/StatesFilter';
import { availableStates } from '@duke-hq/svc-api/lib/states';
import { constructURL } from '@duke-hq/lib';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import { States, StatesSchema } from '@duke-hq/svc-api-db/schemas/enums/States.schema';
import { z } from 'zod';
import { testIds } from './SitesLocations.testIds';
import { emptyPaginationResponse, SortOrder, SortOrderSchema } from '@duke-hq/lib/pagination';

const siteLocationFiltersSchema = z.object({
  orderBy: z.string().optional(),
  sort: SortOrderSchema.optional(),
  state: StatesSchema.optional(),
  page: z.coerce.number().int().positive().optional(),
});

export type SortFilters = z.infer<typeof siteLocationFiltersSchema>;

export function SitesLocations() {
  const navigate = useNavigate();
  const { providerId, siteId } = useParams<{
    providerId: string;
    siteId: string;
  }>();

  const { currentPage, pageSize, setPage } = usePagination();
  const [stateFilter, setStateFilter] = useState<States | undefined>(undefined);
  const [search, setSearch] = useState('');

  const [searchParams, setSearchParams] = useSearchParams({
    orderBy: 'available',
    sort: SortOrder.DESC,
    page: '1',
  });

  const urlFilters = useMemo((): SortFilters => {
    const urlFilters = Object.fromEntries(searchParams.entries());

    // Parse and validate the filters
    const parseResult = siteLocationFiltersSchema.safeParse({
      orderBy: urlFilters.orderBy,
      sort: urlFilters.sort,
      state: stateFilter || undefined,
      page: urlFilters.page != null ? Number(urlFilters.page) : undefined,
    });

    // Return parsed result or empty object if parsing fails
    return parseResult.success ? parseResult.data : { page: 1 };
  }, [searchParams, stateFilter]);

  const handleFilterChange = useCallback(
    (newFilters: Partial<SortFilters>) => {
      setSearchParams(
        new URLSearchParams(
          Object.fromEntries(
            Object.entries({
              ...Object.fromEntries(searchParams.entries()),
              ...newFilters,
            })
              // Filter out null/empty values and convert all values to strings
              .filter(([key, value]) => {
                // Remove page if it's '1' (default)
                if (key === 'page' && value === '1') {
                  return false;
                }

                return value != null && value !== '';
              })
              .map(([key, value]) => [key, String(value)]),
          ),
        ),
      );
    },
    [searchParams, setSearchParams],
  );

  const handleState = useCallback(
    (state: States | undefined) => {
      setStateFilter(state);
      handleFilterChange({ ...urlFilters, state, page: 1 });
    },
    [handleFilterChange, urlFilters],
  );

  const { isOpen } = useDisclosure({
    isOpen: typeof siteId !== 'string' || siteId === '',
  });

  const onCloseHandler = useCallback(() => {
    const url = constructURL(
      `/providers/${providerId}/locations`,
      Object.fromEntries(searchParams.entries()) as Record<string, string>,
    );
    navigate(url);
  }, [navigate, providerId, searchParams]);

  if (providerId == null) {
    return <LocationError message={t`An error occurred, providerId is required.`} />;
  }

  const { data: totalAvailableSites } =
    trpcAdmin.providers.locations.countAvailableSiteLocations.useQuery(
      {
        providerId: parseInt(providerId),
        filters: {
          state: stateFilter,
        },
      },
      {
        enabled: Boolean(providerId),
      },
    );

  const { data: sites, isFetching } =
    trpcAdmin.providers.locations.listSiteLocationsWithPagination.useQuery(
      {
        search: search.trim(),
        filters: urlFilters,
        providerId: parseInt(providerId),
        pagination: {
          page: Number(currentPage),
          limit: pageSize,
        },
      },
      {
        enabled: Boolean(providerId),
        initialData: emptyPaginationResponse(),
        placeholderData: (previousData) => previousData,
      },
    );

  return (
    <>
      {siteId != null ? (
        <Drawer isOpen={isOpen} onClose={onCloseHandler} size="xl">
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton onClick={onCloseHandler} />
            <Suspense fallback={<CenteredSpinner />}>
              <SiteLocationsDetails />
            </Suspense>
          </DrawerContent>
        </Drawer>
      ) : null}

      <TableViewLayout
        pageActions={[]}
        filters={
          <>
            <StatesFilter
              stateFilter={stateFilter}
              stateOptions={availableStates}
              setStateFilter={handleState}
            />
            <InputGroup maxW="xs" size="sm">
              <SearchInput onCommit={setSearch} maxW="search-input" data-testid={testIds.search} />
            </InputGroup>
          </>
        }
        tableActionsLeft={
          <>
            <Box as="span">
              <Text fontSize="md" fontWeight="semibold" color="fg.subtle">
                <Plural
                  value={sites.pagination.totalCount || 0}
                  zero="0 sites"
                  one="1 site"
                  other="# sites"
                />
              </Text>
            </Box>
            <Box as="span">
              <Text fontSize="md" fontWeight="semibold" color="fg.subtle">
                ({totalAvailableSites} <Trans>available</Trans>)
              </Text>
            </Box>
          </>
        }
        table={
          <>
            <SitesLocationsTable
              sites={sites.items}
              urlParams={urlFilters}
              onFilters={handleFilterChange}
            />

            {isFetching ? (
              <Center p={8}>
                <Spinner size="xl" transitionDelay={'2900ms'} />
              </Center>
            ) : sites.items.length === 0 ? (
              <Center p={8}>
                <Text textAlign="center">
                  <Trans>No sites found</Trans>
                </Text>
              </Center>
            ) : null}
          </>
        }
        pagination={
          sites.items.length > 0 ? (
            <Pagination pagination={sites.pagination} onPageChange={setPage} />
          ) : null
        }
      />
    </>
  );
}
